/**
 * Jovii — WHERE THE REMOTE GOES NEXT.
 *
 * The arithmetic behind `dpad.ts`, in a plain module with no DOM in it, because
 * "does Down land on the next rail or on a scroll arrow?" is a question about
 * rectangles and a question about rectangles should be answerable without a
 * television in the room. `dpad.ts` measures the page and applies the answer;
 * everything that DECIDES lives here and is tested in `dpad-rules.test.ts`.
 *
 * NOT TO BE CONFUSED WITH `focus-rules.ts`, which sits next to it and answers a
 * different question. That one is "what is the browse layer LOOKING at" — the
 * leftmost card of the row nearest the middle, which is what drives the trailer
 * dwell and the lifted tile under a thumb. This one is "the viewer pressed Down,
 * what should hold the keyboard focus now". One is ambient and follows the page;
 * the other is deliberate and moves the page. They must not be merged: a phone
 * has the first and no second, a television has both and they disagree
 * constantly (the thing you are looking at on a TV is the thing you have
 * focused, which is precisely why the ambient answer is redundant there).
 *
 * ── LEFT/RIGHT IS WITHIN A RAIL. UP/DOWN IS BETWEEN RAILS. ──────────────────
 *
 * That is the owner's brief in his own words — "jumping from one rail to the
 * other" — and it is also simply what a television does. Pure geometry does not
 * give you it: a rail's heading carries an "All" link and two scroll arrows, and
 * from the rightmost tile of a rail those are the nearest things downward, so an
 * honest nearest-neighbour search puts a 19px text link between every pair of
 * rails. So geometry is CONSTRAINED by groups:
 *
 *   • A candidate may belong to a GROUP with an AXIS ('row' for a rail, 'col'
 *     for a stacked list). `dpad.ts` reads those off `data-dpad` attributes.
 *   • Moving ALONG a group's axis searches only inside that group. Right from a
 *     tile can only ever be another tile of the same rail.
 *   • Moving ACROSS it leaves the group, and prefers landing in another group —
 *     another rail, or a stacked list — over any loose control that happens to
 *     sit between the two. Only when there is no group that way does it consider
 *     everything else, and the app's own floating furniture comes last of all.
 *
 * ── WHAT "IN THAT DIRECTION" MEANS ─────────────────────────────────────────
 *
 * Strictly past the trailing edge, and deliberately not "its centre is beyond my
 * centre". A poster tile is a link with a save button drawn over its top right
 * corner; under the centre rule, Left from tile 2 is a coin-toss between tile 1
 * and tile 1's save button, because the two share a trailing edge to the pixel.
 * Under the strict rule the save button is not to the left of anything and the
 * ambiguity never arises. Tab order is untouched — every control on the page is
 * still reachable with a keyboard, this only decides where an ARROW goes.
 *
 * There is no wrapping and no relaxation. When nothing qualifies the answer is
 * null, and `dpad.ts` treats null as "let the browser have the key", which is
 * what keeps a long page of prose scrollable with the arrows it has always been
 * scrollable with.
 */

/* ────────────────────────────────────────────────────────────────────────────
 * Shapes
 * ──────────────────────────────────────────────────────────────────────────── */

export type Direction = 'up' | 'down' | 'left' | 'right';

/** A rail runs along 'row'; a stacked list runs along 'col'. */
export type Axis = 'row' | 'col';

/** One candidate's rectangle, in viewport coordinates, plus its group. */
export interface NavRect {
  id: string;
  left: number;
  top: number;
  right: number;
  bottom: number;
  /**
   * The nearest enclosing d-pad group, or null for a candidate that stands on
   * its own (a form field, a cell of a wrapping grid). Two candidates are in the
   * same group when these are equal and not null.
   */
  group?: string | null;
  /** The axis of `group`. Meaningless when `group` is null. */
  axis?: Axis | null;
  /**
   * True for the app's own furniture — the top bar, the phone's tab bar. It
   * changes nothing about movement; it only keeps the FIRST press of the remote
   * from landing on the avatar menu when there is a banner underneath it.
   */
  chrome?: boolean;
  /**
   * SCROLLED OUT OF ITS OWN RAIL — present in the DOM, clipped out of sight.
   *
   * A rail of thirty posters is thirty rectangles, and the twenty that have been
   * scrolled past sit at NEGATIVE x, hundreds of pixels off the left of the
   * screen. Inside the rail that is exactly right: Right from tile 9 must find
   * tile 10 whether or not it has been drawn yet, and `dpad.ts` then scrolls the
   * rail to show it. From ANYWHERE ELSE it is a trap, and a measured one — with
   * rail one scrolled 2672px, Left from the avatar menu in the corner of the
   * screen found a poster whose right edge was at -2205, because -2205 is
   * genuinely to the left of 32. The focus vanished into a rail nobody could see
   * and the page jumped 451px to chase it.
   *
   * So a clipped candidate is reachable ALONG its own rail and by nothing else.
   */
  clipped?: boolean;
}

export interface Viewport {
  width: number;
  height: number;
}

/* ────────────────────────────────────────────────────────────────────────────
 * The weights
 * ──────────────────────────────────────────────────────────────────────────── */

/**
 * How much a sideways miss costs, relative to a pixel of travel in the direction
 * actually pressed.
 *
 * Four, and it wants to be high. Moving Down from a tile 400px above the next
 * rail, every tile of that rail is 400px away and the only thing separating them
 * is how far sideways they are — so the sideways term has to dominate at that
 * range or Down from the middle of one rail lands wherever the next rail happens
 * to have been scrolled to. At weight 4 a tile one column off (148px + a gap)
 * costs 640, which comfortably outweighs any plausible difference in the
 * vertical term between two members of one row.
 */
export const ORTHO_WEIGHT = 4;

/**
 * A gentler pull towards the candidate whose middle lines up with ours, used to
 * separate things that OVERLAP sideways and so both score `ortho === 0`.
 *
 * This is the term that decides Left out of a poster: tile 1's link and tile 1's
 * save button end on the same pixel, so the gap term ties, and the link — whose
 * middle is the tile's middle — wins on this. Half a pixel per pixel, so it can
 * never overturn a genuine difference in distance.
 */
export const CENTRE_WEIGHT = 0.5;

/**
 * How much overlap is forgiven when asking "is it past my trailing edge".
 *
 * Two pixels, for sub-pixel layout only: a rail's tiles are laid out by flexbox
 * at fractional widths and two neighbours can measure 0.5px into each other.
 * Anything larger would start letting a control that sits ON another one count
 * as being beside it, which is the ambiguity this rule exists to remove.
 */
export const EDGE_SLACK = 2;

/* ────────────────────────────────────────────────────────────────────────────
 * Geometry
 * ──────────────────────────────────────────────────────────────────────────── */

function horizontal(dir: Direction): boolean {
  return dir === 'left' || dir === 'right';
}

/** How far past `from`'s trailing edge `r` starts. Negative means it does not. */
function travel(from: NavRect, r: NavRect, dir: Direction): number {
  switch (dir) {
    case 'right':
      return r.left - from.right;
    case 'left':
      return from.left - r.right;
    case 'down':
      return r.top - from.bottom;
    case 'up':
      return from.top - r.bottom;
  }
}

/** The gap between the two rectangles on the OTHER axis; 0 when they overlap. */
function sideways(from: NavRect, r: NavRect, dir: Direction): number {
  const [aFrom, aTo, bFrom, bTo] = horizontal(dir)
    ? [from.top, from.bottom, r.top, r.bottom]
    : [from.left, from.right, r.left, r.right];
  if (bTo <= aFrom) return aFrom - bTo;
  if (bFrom >= aTo) return bFrom - aTo;
  return 0;
}

/** Distance between the two middles on the OTHER axis. */
function offCentre(from: NavRect, r: NavRect, dir: Direction): number {
  const mine = horizontal(dir) ? (from.top + from.bottom) / 2 : (from.left + from.right) / 2;
  const theirs = horizontal(dir) ? (r.top + r.bottom) / 2 : (r.left + r.right) / 2;
  return Math.abs(mine - theirs);
}

/** Lower is better. Only ever compared between candidates of the same tier.
 *  Exported for `dpad.ts`'s band-pruning proof: a candidate outside the band
 *  has `travel > AHEAD`, and since score ≥ travel, a pruned winner whose score
 *  is ≤ AHEAD could not have been beaten by anything the band excluded. */
export function score(from: NavRect, r: NavRect, dir: Direction): number {
  return (
    travel(from, r, dir) +
    sideways(from, r, dir) * ORTHO_WEIGHT +
    offCentre(from, r, dir) * CENTRE_WEIGHT
  );
}

/** The best of a pool, or null when nothing in it lies in the direction asked. */
function nearest(from: NavRect, pool: readonly NavRect[], dir: Direction): NavRect | null {
  let winner: NavRect | null = null;
  let best = Infinity;
  for (const r of pool) {
    if (r.id === from.id) continue;
    if (r.right - r.left <= 0 || r.bottom - r.top <= 0) continue;
    if (travel(from, r, dir) < -EDGE_SLACK) continue;
    const s = score(from, r, dir);
    if (s < best) {
      best = s;
      winner = r;
    }
  }
  return winner;
}

/* ────────────────────────────────────────────────────────────────────────────
 * The answer
 * ──────────────────────────────────────────────────────────────────────────── */

/**
 * THE FLOATING BAR THAT OVERLAPS YOU, considered only when Up has nowhere else
 * to go.
 *
 * The top bar has no bar: its pills FLOAT over the artwork, and on the home
 * screen they sit INSIDE the full-bleed banner's own rectangle. "Past my
 * trailing edge" — the rule that keeps a save button from counting as beside
 * its own card — therefore excludes them, and the measured result was a remote
 * that could never reach Shows, Movies, Search or the profile at all: Up on the
 * banner did nothing, for ever.
 *
 * So when the strict search has already said null in every tier, Up alone gets
 * one more look: chrome whose CENTRE is above ours, scored by how far. Chrome
 * only — the one thing in this app that legitimately floats over content is its
 * own furniture — and Up only, because "go to the bar" is what Up at the top of
 * a page means, while Right into the avatar would be a jump nobody asked for.
 */
function overhead(from: NavRect, chrome: readonly NavRect[]): NavRect | null {
  const mine = (from.top + from.bottom) / 2;
  let winner: NavRect | null = null;
  let best = Infinity;
  for (const r of chrome) {
    if (r.id === from.id) continue;
    if (r.right - r.left <= 0 || r.bottom - r.top <= 0) continue;
    const theirs = (r.top + r.bottom) / 2;
    if (theirs >= mine) continue;
    const s = mine - theirs + sideways(from, r, 'up') * ORTHO_WEIGHT + offCentre(from, r, 'up') * CENTRE_WEIGHT;
    if (s < best) {
      best = s;
      winner = r;
    }
  }
  return winner;
}

/**
 * WHERE THE FOCUS GOES when `dir` is pressed while `fromId` holds it, or null
 * when nothing lies that way and the key belongs to the browser.
 *
 * `fromId` not being in `rects` means whatever held the focus has been torn off
 * the page — a rail replaced by its next page of results, a sheet closed under
 * the remote — and the honest answer is to start again from the top of the
 * screen rather than to guess.
 */
export function nextId(
  rects: readonly NavRect[],
  fromId: string | null,
  dir: Direction,
  view: Viewport
): string | null {
  const from = fromId === null ? null : (rects.find((r) => r.id === fromId) ?? null);
  if (from === null) return entryId(rects, view);

  const inGroup = from.group !== null && from.group !== undefined;
  const along = inGroup && (from.axis === 'row') === horizontal(dir);

  /* ALONG the rail: only the rail. Right from a tile is another tile or it is
     nothing at all — never the heading's "All" link, never the rail below.

     ONE exception, for the axis that stacks: a 'col' list's vertical ends open
     back onto the page. A rail's Left/Right dead-ends are harmless — the page
     does not continue sideways — but Up from the first episode of a season has
     a season picker sitting right above it, and answering null there left the
     remote pinned to episode one while the browser crept the page around it.
     'row' keeps the null: handing the key back is what lets a prose page
     scroll. */
  if (inGroup && along) {
    const siblings = rects.filter((r) => r.group === from.group);
    const inside = nearest(from, siblings, dir);
    if (inside !== null) return inside.id;
    if (from.axis !== 'col') return null;
  }

  /* ACROSS it: out of the group, groups first, the app's own furniture last.

     The tiered search is the whole point. From the rightmost tile of a rail,
     the nearest thing downward on a real page is the NEXT RAIL'S HEADING — its
     "All" link sits at the right-hand end, 40px below, near-perfectly aligned —
     and landing there is how TV navigation acquires the extra hops that make it
     feel like wading. A GROUP is what Down means — the next rail, or a stacked
     list such as a season's episodes (which is why this tier takes any axis,
     not just its own: 'row' chips sit directly above a 'col' episode list, and
     matching axes leapt clean over twenty episodes to the rail below them).

     Chrome comes last for the same reason it is excluded from the first press:
     the bar FLOATS at the top of the viewport, so mid-page it is always "just
     above" wherever the focus is, and letting it compete meant Up out of a
     season picker landed on the search pill while the page still had a
     trailer shelf and a Play button to offer. The bar is reached when the
     content is exhausted, which is what the top of a page means. */
  const outside = rects.filter(
    (r) => (!inGroup || r.group !== from.group) && r.clipped !== true
  );

  /* INSIDE THE CHROME, SIDEWAYS STAYS IN THE CHROME.

     The demotion below — furniture last — exists so a bar that FLOATS over the
     page cannot catch a mid-page Up. But it is about ENTERING the bar, not
     about moving along it once there, and demoting both broke the bar's own
     row: with the focus on the Movies pill, Left was answered by a rail tile
     two hundred pixels below (the tile tier never runs dry) and the pill
     beside it — chrome, last tier — was never consulted. Measured on the
     owner's Fire TV Stick, replicated at 1280x720: Left from Movies landed on
     Top 10's second tile, Right on its fourth.

     So a press that STARTS on chrome asks the chrome first, sideways only.
     Sideways, because the bar is a line: Left and Right walk it, exactly as
     TopBar's own header comment always promised. Up and Down keep the ordinary
     tiers, which is what lets Down step off the bar into the page — and what
     keeps the phone's bottom tab bar from teleporting Up-presses clean over
     the content to the bubbles at the top. */
  if (from.chrome === true && horizontal(dir)) {
    const beside = nearest(from, outside.filter((r) => r.chrome === true), dir);
    if (beside !== null) return beside.id;
  }

  if (inGroup) {
    const grouped = outside.filter(
      (r) => r.group !== null && r.group !== undefined && r.chrome !== true
    );
    const best = nearest(from, grouped, dir);
    if (best !== null) return best.id;
  }
  const calm = outside.filter((r) => r.chrome !== true);
  const found = nearest(from, calm, dir);
  if (found !== null) return found.id;
  const chrome = outside.filter((r) => r.chrome === true);
  const bar = nearest(from, chrome, dir);
  if (bar !== null) return bar.id;
  if (dir === 'up') return overhead(from, chrome)?.id ?? null;
  return null;
}

/**
 * WHERE THE FIRST PRESS LANDS.
 *
 * The top-left-most thing that is really on the screen, with the app's own
 * furniture considered last. On a television that is the difference between
 * pressing Down and lighting up the banner's Play button — which is what the
 * page is FOR — and pressing Down and lighting up the avatar menu, which is a
 * pill in the corner that happens to be four pixels higher.
 *
 * Only what is on screen qualifies, because the first press has to be visibly
 * answered; a focus that lands 900px below the fold reads as nothing happening.
 */
export function entryId(rects: readonly NavRect[], view: Viewport): string | null {
  const onScreen = rects.filter(
    (r) =>
      r.clipped !== true &&
      r.right > 0 &&
      r.left < view.width &&
      r.bottom > 0 &&
      r.top < view.height &&
      r.right - r.left > 0 &&
      r.bottom - r.top > 0
  );
  if (onScreen.length === 0) return null;

  const body = onScreen.filter((r) => r.chrome !== true);
  const pool = body.length > 0 ? body : onScreen;

  let winner = pool[0]!;
  for (const r of pool) {
    // A row's worth of tolerance on the vertical, so that two controls side by
    // side are separated by which is further LEFT and not by a pixel of baseline.
    if (r.top < winner.top - 24 || (r.top < winner.top + 24 && r.left < winner.left)) winner = r;
  }
  return winner.id;
}
